import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useParams,
  useLocation,
} from 'react-router-dom';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import ApolloProvider from '@/graphql/ApolloProvider';
import { ConfigProvider } from '@/components/Providers/ConfigProvider/ConfigProvider';
import { NotificationProvider } from '@/components/Providers/NotificationProvider/NotificationProvider';
import { RedirectInfoProvider } from '@/components/Providers/RedirectInfoProvider/RedirectInfoProvider';
import {
  BrandingProvider,
  useAuthBranding,
} from '@/components/Providers/BrandingProvider/BrandingProvider';
import { AccessibilityProvider } from '@/components/Providers/AccessibilityProvider/AccessibilityProvider';
import { ThemeProviderNext } from '@/components/Providers/ThemeProvider/ThemeProvider';
import { Container } from '@/components/Elements/Container/Container';
import { LanguagePicker } from '@/components/Elements/LanguagePicker/LanguagePicker';
import { REDIRECT_APP } from '@/constants/authentication';
import { RedirectApp } from '@/enums/RedirectApp';
import Logo from '@/assets/logos/SpottedZebraLogo.svg';
import styles from '@/root.module.scss';
import clsx from 'clsx';
import { defaultLocale, locales } from '@/constants/locale';
import { getLegacyAuthRedirectUrl } from '@/utils/legacyAuthRedirect';
import { getLocalizedPath } from '@/utils/localePath';

// Page imports
import SignInPage from '@/pages/sign-in/page';
import SignUpPage from '@/pages/sign-up/page';
import ResetPasswordPage from '@/pages/reset-password/page';
import SetNewPasswordPage from '@/pages/set-new-password/page';
import TwoFactorAuthPage from '@/pages/two-factor-authentication/page';
import SSOCallbackPage from '@/pages/sso-callback/page';
import LandingPage from '@/pages/page';

// Roboto font import
import '@fontsource/roboto/400.css';
import '@fontsource/roboto/500.css';

import '@/authBranding.scss';

function App() {
  return (
    <BrowserRouter>
      <div className={clsx('roboto', styles.content)}>
        <ConfigProvider>
          <ApolloProvider>
            <NotificationProvider>
              <RedirectInfoProvider>
                <AccessibilityProvider>
                  <ThemeProviderNext>
                    <BrandingProvider>
                      <Routes>
                        {/* Redirect root to default locale */}
                        <Route
                          path="/"
                          element={
                            <Navigate
                              to={`/${defaultLocale}/sign-in`}
                              replace
                            />
                          }
                        />

                        {/* Legacy product apps auth bridge routes */}
                        <Route
                          path="/auth/*"
                          element={<LegacyAuthRedirect />}
                        />
                        <Route path="/inv" element={<LegacyAuthRedirect />} />

                        {/* All routes under /:lang */}
                        <Route path="/:lang/*" element={<Layout />} />

                        {/* Fallback for invalid routes */}
                        <Route
                          path="*"
                          element={
                            <Navigate
                              to={`/${defaultLocale}/sign-in`}
                              replace
                            />
                          }
                        />
                      </Routes>
                    </BrandingProvider>
                  </ThemeProviderNext>
                </AccessibilityProvider>
              </RedirectInfoProvider>
            </NotificationProvider>
          </ApolloProvider>
        </ConfigProvider>
      </div>
    </BrowserRouter>
  );
}

// Layout component that wraps all routes
function Layout() {
  const params = useParams();
  const lang = params.lang || defaultLocale;
  const { i18n } = useTranslation();
  const location = useLocation();

  const redirectApp =
    new URLSearchParams(location.search).get(REDIRECT_APP) ||
    localStorage.getItem(REDIRECT_APP);
  const isCandidateRedirect =
    !redirectApp || redirectApp === RedirectApp.CANDIDATE;

  // Company branding, published by views that resolve a project's public
  // co-branding (direct invites to co-branded / candidate-portal roles).
  const { branding } = useAuthBranding();
  const brandingStyle = branding?.accentColor1
    ? ({ '--auth-accent-1': branding.accentColor1 } as React.CSSProperties)
    : undefined;

  const isValidLang = locales.includes(lang);

  // Sync i18n language with URL param
  useEffect(() => {
    if (isValidLang && i18n.language !== lang) {
      i18n.changeLanguage(lang);
      localStorage.setItem('language', lang);
    }
  }, [lang, isValidLang, i18n]);

  // An unknown first segment (e.g. a link to "/sign-in" without a language)
  // is rewritten before any page renders. Doing this in an effect let the
  // page below mount with the bogus language, fire its own redirect, and then
  // sit on its loader forever once the URL was rewritten (PDE-914).
  if (!isValidLang) {
    return (
      <Navigate
        to={getLocalizedPath(location.pathname, location.search)}
        replace
      />
    );
  }

  return (
    <main
      className={branding?.accentColor1 ? 'branded-auth' : undefined}
      style={brandingStyle}
    >
      <Container>
        <header className={styles.header}>
          {!branding && <img src={Logo} alt="" />}
          {branding?.companyLogoUrl && (
            <img
              src={branding.companyLogoUrl}
              alt=""
              className={styles.brandLogo}
            />
          )}
          {isCandidateRedirect && <LanguagePicker />}
        </header>
        <Routes>
          <Route index element={<LandingPage />} />
          <Route path="sign-in" element={<SignInPage />} />
          <Route path="sign-up" element={<SignUpPage />} />
          <Route path="reset-password" element={<ResetPasswordPage />} />
          <Route path="set-new-password" element={<SetNewPasswordPage />} />
          <Route
            path="two-factor-authentication"
            element={<TwoFactorAuthPage />}
          />
          <Route path="sso-callback" element={<SSOCallbackPage />} />
          <Route
            path="*"
            element={
              <Navigate to={`/${lang || defaultLocale}/sign-in`} replace />
            }
          />
        </Routes>
      </Container>
    </main>
  );
}

function LegacyAuthRedirect() {
  const location = useLocation();

  return (
    <Navigate
      to={getLegacyAuthRedirectUrl(
        location.pathname,
        location.search,
        defaultLocale
      )}
      replace
    />
  );
}

export default App;
